import { useEffect, useRef, useState } from 'react';

const makeId = () =>
  globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const getFilenameFromPath = (p = '') => {
  const s = String(p);
  const parts = s.split('/');
  return parts[parts.length - 1] || s;
};

const normalizeServerFile = f => {
  const serverPath = f.NOME;
  const filename = getFilenameFromPath(serverPath);

  return {
    id: serverPath,
    file: { name: filename, size: 0 },
    progress: 100,
    status: 'done',
    fromServer: true,
    server: {
      UUID: f.UUID,
      NOME: f.NOME
    }
  };
};

const useUploadDocumentoAssinadoHook = ({
  onFilesChange,
  arquivosEnviados = [],
  onRemoveServerFile,
  onUploadedFilesChange
}) => {
  const inputRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const acceptedExtensions = ['pdf', 'xml', 'zip'];

  useEffect(() => {
    if (!Array.isArray(arquivosEnviados)) {
      setUploadedFiles([]);
      return;
    }

    setUploadedFiles(arquivosEnviados.slice().reverse().map(normalizeServerFile));
  }, [arquivosEnviados]);

  useEffect(() => {
    onUploadedFilesChange?.(uploadedFiles);
  }, [uploadedFiles, onUploadedFilesChange]);

  const handleClick = () => inputRef.current?.click();

  const isValidFile = file => {
    const ext = file.name.split('.').pop()?.toLowerCase();
    return acceptedExtensions.includes(ext);
  };

  const updateItem = (id, patch) => {
    setUploadedFiles(prev => prev.map(it => (it.id === id ? { ...it, ...patch } : it)));
  };

  const uploadOne = async localItem => {
    try {
      updateItem(localItem.id, { status: 'loading', progress: 0 });

      const savedPaths = await onFilesChange?.([localItem.file], percent => {
        updateItem(localItem.id, { progress: percent });
      });

      const serverPath = Array.isArray(savedPaths) ? savedPaths[0] : null;

      if (serverPath) {
        const filename = getFilenameFromPath(serverPath);

        updateItem(localItem.id, {
          status: 'done',
          progress: 100,
          fromServer: true,
          id: serverPath,
          file: { ...localItem.file, name: filename },
          server: {
            NOME: serverPath
          }
        });
      } else {
        updateItem(localItem.id, { status: 'done', progress: 100 });
      }
    } catch (e) {
      updateItem(localItem.id, { status: 'error', progress: 0 });
    }
  };

  const handleFiles = files => {
    const validFiles = Array.from(files || []).filter(isValidFile);
    if (!validFiles.length) return;

    const mapped = validFiles.map(file => ({
      id: makeId(),
      file,
      progress: 0,
      status: 'loading',
      fromServer: false
    }));

    setUploadedFiles(prev => [...mapped, ...prev]);
    mapped.reduce((promise, item) => promise.then(() => uploadOne(item)), Promise.resolve());
  };

  const handleDrop = event => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
    handleFiles(event.dataTransfer.files);
  };

  const handleDragOver = event => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = event => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
  };

  const handleInputChange = event => {
    handleFiles(event.target.files);
    event.target.value = '';
  };

  const handleRemove = async id => {
    const item = uploadedFiles.find(x => x.id === id);
    if (!item) return;

    if (item.fromServer) {
      try {
        updateItem(id, { status: 'loading' });
        await onRemoveServerFile?.(item.server);
        setUploadedFiles(prev => prev.filter(x => x.id !== id));
      } catch (e) {
        updateItem(id, { status: 'error' });
      }
      return;
    }

    setUploadedFiles(prev => prev.filter(x => x.id !== id));
  };

  const formatSizeKb = size => {
    const bytes = Number(size ?? 0);
    const kb = bytes / 1024;
    if (kb < 1024) return `${Math.max(1, kb).toFixed(1)} KB`;
    return `${(kb / 1024).toFixed(1)} MB`;
  };

  const getStatusText = status => {
    if (status === 'loading') return 'Carregando...';
    if (status === 'error') return 'Erro ao carregar arquivo';
    return 'Concluído';
  };

  return {
    inputRef,
    handleInputChange,
    handleClick,
    handleDrop,
    handleDragOver,
    handleDragLeave,
    isDragOver,
    uploadedFiles,
    formatSizeKb,
    handleRemove,
    getStatusText
  };
};

export default useUploadDocumentoAssinadoHook;
